import { useState } from 'react';
import { Trans, useTranslation } from 'next-i18next/pages';
import { useQueryClient } from '@tanstack/react-query';
import { captureScrollForAuth, restoreScrollAfterAuth } from '@pkgs/components/utils/preserveScrollDuringAuth';
import { type PageTrackingTypes } from '@pkgs/components/types/tracking';
import { type TFunction } from '@pkgs/components/index';
import { usePreserveScrollDuringAuth } from '@pkgs/components/hooks/usePreserveScrollDuringAuth';
import { SignInButton } from '@ev-tech/user-account-modules';
import { useStatusMessage } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { alertVariants } from '@ev-tech/ui-core-components/Alert';

import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { removeListingQueriesWithUserId } from '@/hooks/queries/queryKeys';

export const UserAccount = ({
  countryCode,
  language,
  onSignInFlyoutOpen,
  pageTrackingTypes,
  shopId,
}: {
  countryCode: string;
  language: string;
  onSignInFlyoutOpen: () => void;
  pageTrackingTypes?: PageTrackingTypes;
  shopId?: string;
}) => {
  const queryClient = useQueryClient();
  const { showMessage } = useStatusMessage();
  const { t } = useTranslation(TRANSLATION_NAMESPACE.user_account);
  const [isAuthFlyoutOpen, setIsAuthFlyoutOpen] = useState(false);

  usePreserveScrollDuringAuth(isAuthFlyoutOpen);

  return (
    <SignInButton
      tracking={{
        pageType: pageTrackingTypes?.pageType ?? '',
        templateType: pageTrackingTypes?.templateType ?? '',
        shopId,
      }}
      tFunc={t as TFunction}
      country={countryCode.toLowerCase()}
      language={language}
      onSignInFlyoutOpen={onSignInFlyoutOpen}
      onSignInFlyoutOpenChange={(isOpen) => {
        setIsAuthFlyoutOpen(isOpen);
        if (isOpen) {
          captureScrollForAuth();
        }
      }}
      onSignInSuccess={() => {
        restoreScrollAfterAuth();
        showMessage(t('userAccount.signInSuccess.message'), { alertType: alertVariants.success });
      }}
      onEmailVerificationSuccess={() => {
        showMessage(t('userAccount.emailVerificationSuccess.message'), { alertType: alertVariants.success });
      }}
      onEmailVerificationFailure={() => {
        showMessage(t('userAccount.emailVerificationFailure.message'), { alertType: alertVariants.error });
      }}
      onSignOutSuccess={() => {
        removeListingQueriesWithUserId(queryClient);
        restoreScrollAfterAuth();
        showMessage(t('userAccount.signOutSuccess.message'), { alertType: alertVariants.success });
      }}
      onResetPasswordSuccess={() => {
        showMessage(t('userAccount.resetPasswordSuccess.message'), { alertType: alertVariants.success });
      }}
      onSignUpSuccess={() => {
        restoreScrollAfterAuth();
        showMessage(
          <Trans
            t={t}
            i18nKey="userAccount.signUpSuccess.message"
            components={{
              bold: <b />,
            }}
          />,
          { alertType: alertVariants.success },
        );
      }}
    />
  );
};
